<template>
  <Section>
    <template v-slot:section-header>
      <h5 class="title">รายละเอียด</h5>
    </template>
    <template v-slot:section-body>
      <form>
        <div class="row">
          <div class="col-md-8">
            <FormGroup :errorMessage="getErrorMessage('programId')">
              <h5 class="name">รายการ</h5>
              <SelectOptionObject
                :value="currentEpisode.program"
                :options="programs"
                :label="'title'"
                :placeholder="'เลือกรายการ'"
                :class="{'is-invalid': getErrorMessage('programId') }"
                :removeable="false"
                @onChanged="onSelectedProgram"
                @onSearch="loadPrograms" />
              <div
                class="invalid-feedback"
                v-if="getErrorMessage('programId')">
                กรุณาเลือกรายการ
              </div>
            </FormGroup>
            <FormGroup>
              <h5 class="name">หมวดหมู่คอนเทนท์</h5>
              <SelectOptionObject
                :class="{'is-invalid': getErrorMessage('contentCategory') }"
                :value="currentEpisode.contentCategory"
                :disabledDeleteButton="true"
                :allowEmpty="false"
                :options="contentCategories"
                :disabled="hasContentCategory()"
                :multiple="true"
                @onChanged="onSelectedContentCategory" />
              <div
                class="invalid-feedback"
                v-if="getErrorMessage('contentCategory')">
                {{getErrorMessage('contentCategory')}}
              </div>
            </FormGroup>
            <FormGroup v-if="hasSeasons()">
              <h5 class="name">ซีซั่น</h5>
              <SelectOptionObject
                :value="currentEpisode.programSeason"
                :options="seasons"
                :label="'title'"
                :placeholder="'เลือกซีซั่น'"
                :allowEmpty="false"
                :multiple="true"
                @onChanged="onSelectedSeasons"/>
            </FormGroup>
            <FormGroup>
              <h5 class="name">ช่วงอายุ</h5>
              <SelectOptionObject
                :value="currentEpisode.ageCategory"
                :options="ageCategories"
                :placeholder="'เลือกช่วงอายุ'"
                :allowEmpty="false"
                :multiple="true"
                @onChanged="onSelectedAgeCategory" />
            </FormGroup>
            <FormGroup>
              <h5 class="name">รายวิชา</h5>
              <SelectOptionObject
                :value="currentEpisode.subjectCategory"
                :options="subjectCategories"
                :placeholder="'เลือกรายวิชา'"
                :allowEmpty="false"
                :multiple="true"
                @onChanged="onSelectedSubjectCategory" />
            </FormGroup>
            <FormGroup :errorMessage="getErrorMessage('title')">
              <h5 class="name">ชื่อตอน</h5>
              <input
                type="text"
                placeholder="ชื่อตอน"
                class="form-control"
                :class="{'is-invalid': getErrorMessage('title')}"
                :value="currentEpisode.title"
                @input="$store.dispatch('setCurrentEpisode', { title: $event.target.value })" />
              <div
                class="invalid-feedback"
                v-if="getErrorMessage('title')">
                 ระบุชื่อตอน
                </div>
            </FormGroup>
            <FormGroup
              class="with-help-text"
              :errorMessage="getErrorMessage('tagline')">
              <h5 class="name">รายละเอียดอย่างสั้น</h5>
              <small class="help-text">
                คำอธิบายสั้นๆ ที่จะถูกใช้ในการแสดงตัวอย่างใน เสิร์ชเอ็นจิ้นและโซเชียลเน็ทเวิร์ค
              </small>
              <textarea
                type="text"
                rows="5"
                class="form-control"
                :class="{'is-invalid': getErrorMessage('tagline')}"
                placeholder="รายละเอียดอย่างสั้น"
                :value="currentEpisode.tagline"
                @input="$store.dispatch('setCurrentEpisode', { tagline: $event.target.value })" />
            </FormGroup>
            <FormGroup>
              <h5 class="name">ออกอากาศครั้งแรก</h5>
              <DateTimePicker
                :value="currentEpisode.firstAiredAt"
                @input="(newDate) => $store.dispatch('setCurrentEpisode', { firstAiredAt: newDate })"
                placeholder="กรุณาเลือกวันที่" />
            </FormGroup>
            <FormGroup>
              <h5 class="name">คำอธิบาย</h5>
              <HtmlEditor
                :description="currentEpisode.description"
                @update:description="(description) => $store.dispatch('setCurrentEpisode', { description })"/>
            </FormGroup>
          </div>
          <div class="col-md-4">
            <FormGroup>
              <h5 class="name">สถานะการเผยแพร่</h5>
              <ContentStatusManagement
                :status="currentEpisode.status"
                :startPublish="currentEpisode.startPublish"
                :endPublish="currentEpisode.endPublish"
                :onChange="onChangeStatus" />
            </FormGroup>
            <FormGroup>
              <h5 class="name">แท็ก</h5>
              <multiselect
                :value="currentEpisode.tags"
                :options="tags"
                :multiple="true"
                :taggable="true"
                :addTag="addTag"
                @onSearch="loadTags"
                @onChanged="(tags) => $store.dispatch('setCurrentEpisode', { tags })" />
            </FormGroup>
            <ManageCollectionItem
              class="mb-4"
              v-if="!isEpisodeCreate()"
              :itemId="currentEpisode.id"
              :collectionType="'episode'">
              เพิ่มตอนลงชุดตอน
            </ManageCollectionItem>
            <FormGroup v-if="currentEpisode && currentEpisode.createdBy">
              <h5 class="name">สร้างโดย</h5>
              <EditorInformation :editor="currentEpisode.createdBy" :timeStamp="currentEpisode.createdAt" />
            </FormGroup>
            <FormGroup v-if="currentEpisode && currentEpisode.updatedBy">
              <h5 class="name">แก้ไขล่าสุดโดย</h5>
              <EditorInformation :editor="currentEpisode.updatedBy" :timeStamp="currentEpisode.updatedAt" />
            </FormGroup>
          </div>
        </div>
      </form>
    </template>
  </Section>
</template>

<script>
import _ from 'lodash';
import { mapState } from 'vuex';
import dateFormetter from '@/utils/dateFormatter';
import { loadPrograms } from '@/program/api';
import { loadTaxonomyByKey } from '@/taxonomy/api';
import SelectOptionObject from '@/components/SelectOptionObject.vue';
import Multiselect from '@/components/MultiSelect.vue';
import FormGroup from '@/components/FormGroup.vue';
import Section from '@/components/Section.vue';
import EditorInformation from '@/components/EditorInformation.vue';
import DateTimePicker from '@/components/DateTimePicker.vue';
import HtmlEditor from '@/htmlEditor/HtmlEditor.vue';
import ContentStatusManagement from '@/contentStatus/ContentStatusManagement.vue';
import ManageCollectionItem from '@/collection/ManageCollectionItem.vue';

export default {
  mixins: [dateFormetter],
  components: {
    Multiselect,
    FormGroup,
    Section,
    ContentStatusManagement,
    HtmlEditor,
    DateTimePicker,
    SelectOptionObject,
    ManageCollectionItem,
    EditorInformation,
  },
  data() {
    return {
      programs: [],
      contentCategories: [],
      tags: [],
      ageCategories: [],
      subjectCategories: [],
      seasons: [],
    };
  },
  created() {
    this.loadContentCategories();
    this.loadTags();
    this.loadPrograms();
    this.loadAge();
    this.loadSubject();
    this.seasons = this.currentEpisode.program.programSeasons;
  },
  computed: {
    ...mapState({
      currentEpisode: (state) => state.episodeItem.currentEpisode,
      saveEpisodeState: (state) => state.episodeItem.saveEpisodeState,
    }),
    program() {
      return this.currentEpisode.programId;
    },
  },
  methods: {
    async loadContentCategories() {
      const response = await loadTaxonomyByKey('content-category');
      this.contentCategories = response.data.data;
    },
    async loadTags(filter) {
      const response = await loadTaxonomyByKey('tag', filter);
      this.tags = response.data.data;
    },
    async loadPrograms(filter) {
      const response = await loadPrograms(filter);
      this.programs = response.data.data;
    },
    async loadAge() {
      const response = await loadTaxonomyByKey('age-category');
      this.ageCategories = response.data.data;
    },
    async loadSubject() {
      const response = await loadTaxonomyByKey('subject-category');
      this.subjectCategories = response.data.data;
    },
    onSelectedProgram(program) {
      if (program) {
        this.seasons = program.programSeasons;
        this.$store.dispatch('setCurrentEpisode', {
          ...this.currentEpisode,
          programId: program.id,
          program,
          programSeason: null,
          programSeasonId: null,
          contentCategory: _.get(program, 'contentCategory', this.currentEpisode.contentCategory),
          contentCategoryId: _.get(program, 'contentCategoryId', this.currentEpisode.contentCategoryId),
        });
      } else {
        // reset episode when program have been removed
        this.$store.dispatch('setCurrentEpisode', {
          ...this.currentEpisode,
          program: null,
          programId: null,
          programSeason: null,
          programSeasonId: null,
          contentCategory: null,
          contentCategoryId: null,
        });
      }
    },
    onSelectedContentCategory(category) {
      if (category) {
        this.$store.dispatch('setCurrentEpisode', { contentCategory: category, contentCategoryId: category.id });
      }
    },
    onSelectedAgeCategory(age) {
      if (age) {
        this.$store.dispatch('setCurrentEpisode', { ageCategory: age });
      } else {
        this.$store.dispatch('setCurrentEpisode', { ageCategory: null });
      }
    },
    onSelectedSubjectCategory(subject) {
      if (subject) {
        this.$store.dispatch('setCurrentEpisode', { subjectCategory: subject });
      } else {
        this.$store.dispatch('setCurrentEpisode', { subjectCategory: null });
      }
    },
    onSelectedSeasons(season) {
      if (season) {
        this.$store.dispatch('setCurrentEpisode', { programSeason: season, programSeasonId: season.id });
      } else {
        this.$store.dispatch('setCurrentEpisode', { programSeason: null, programSeasonId: null });
      }
    },
    isEpisodeCreate() {
      const createEpisode = this.$route.path;
      if (createEpisode.includes('create')) {
        return true;
      }
      return false;
    },
    addTag(tag) {
      this.tags.push(tag);
      this.$store.dispatch('setCurrentEpisode', { tags: [...this.currentEpisode.tags, tag] });
    },
    onChangeStatus(newStatus) {
      this.$store.dispatch('setCurrentEpisode', {
        status: newStatus.status,
        startPublish: newStatus.startPublish,
        endPublish: newStatus.endPublish,
      });
    },
    hasContentCategory() {
      const category = _.get(this.currentEpisode, 'program.contentCategoryId', null);
      if (category) return true;
      return false;
    },
    getErrorMessage(fieldName) {
      return _.get(this.saveEpisodeState.error, `${fieldName}`, null);
    },
    hasSeasons() {
      if (this.currentEpisode.program) {
        if (this.currentEpisode.program.hasSeason) return true;
        return false;
      }
      return false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/components/form.scss';
@import './src/assets/scss/themes/components/section-container.scss';
.cover-image-container {
  margin-bottom: $spacer * 1.5;
}
.social-image-container {
  margin-bottom: $spacer * 1.5;
}


</style>
